import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useI18n } from "@/i18n/I18nProvider";
import { supabaseUntyped } from "@/integrations/supabase/untyped";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import {
  CONFIDENCE_BADGE_CLASS,
  RISK_LABEL_BADGE_CLASS,
  aggregateRiskCounts,
  computeRiskLabel,
  evaluateRowFlags,
  formatRiskReasonLocalized,
  getBatchCapacityContextLocalized,
  getConfidenceCopyLocalized,
  getConfidenceLevel,
  getCountsSummaryLocalized,
  getSuggestedActionLocalized,
  hoursBetween,
  trackSchedulerActionShown,
  trackSchedulerSummaryViewed,
  type ClientFallbackInput,
  type RowRiskFlags,
} from "@/lib/scheduler";
import {
  useBatchSchedulerRows,
  useSchedulerSummary,
} from "@/hooks/scheduler";

type BatchRow = {
  id: string;
  created_at: string;
  rows_count: number;
  status: string;
  raw_text: string | null;
};

type BookingRow = {
  id: string;
  booking_number: string;
  status: string;
  service_id: string;
  service_name?: string | null;
  service_address_text?: string | null;
  total_amount?: number | null;
  window_start_at?: string | null;
  window_end_at?: string | null;
  checkout_at?: string | null;
  checkin_at?: string | null;
  notes?: string | null;
  scheduled_date?: string | null;
  scheduled_time?: string | null;
  batch_id?: string | null;
};

type DayCapacitySummary = {
  date: string;
  totalCapacity: number;
  blocked: boolean;
  configuredSlots: number;
};

/** Raw shape of a booking row returned by the inline `bookings` select. */
type RawBookingRow = {
  id: string;
  booking_number: string;
  status: string;
  service_id: string;
  services?: { name?: string | null } | null;
  service_address_text?: string | null;
  total_amount?: number | string | null;
  window_start_at?: string | null;
  window_end_at?: string | null;
  checkout_at?: string | null;
  checkin_at?: string | null;
  notes?: string | null;
  scheduled_date?: string | null;
  scheduled_time?: string | null;
  batch_id?: string | null;
};

type RawSlotRow = { date: string; slot: string; capacity: number | null; blocked: boolean | null };
type RawDayRow = { date: string; capacity: number | null; blocked: boolean | null };

function fmt(dt: string | null | undefined): string {
  if (!dt) return "—";
  try {
    const d = new Date(dt);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleString();
  } catch {
    return "—";
  }
}

// Risk evaluation, suggested actions and risk-label badges live in
// `src/lib/scheduler` so BulkOrders, the External Bookings Tracker and
// the server-side engine share the same rules and microcopy.
//
// Sprint 5 — engine wiring strategy:
//   * The card LIST keeps the local (client) computation. Computing once
//     per card is cheap; calling 30 RPCs on mount would not be.
//   * The detail SHEET (open batch) goes through `useSchedulerSummary` and
//     `useBatchSchedulerRows`: the server engine returns the canonical
//     `risk_label`, `suggested_action`, `risk_reasons` and `confidence_score`
//     (see `supabase/migrations/20260425113000_scheduler_engine_v1.sql`).
//   * The local computation also feeds the hook's `clientFallback` so the
//     UI degrades gracefully when the RPC is unreachable or the killswitch
//     `VITE_SCHEDULER_SERVER_DISABLED=1` is on.

export default function BulkOrdersPanel({ companyId }: { companyId: string }) {
  const navigate = useNavigate();
  const { t, tx } = useI18n();
  const [loading, setLoading] = useState(false);
  const [batches, setBatches] = useState<BatchRow[]>([]);
  const [byBatchBookings, setByBatchBookings] = useState<Record<string, BookingRow[]>>({});
  const [capacityByDate, setCapacityByDate] = useState<Record<string, DayCapacitySummary>>({});

  const [openBatchId, setOpenBatchId] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [onlyTightWindows, setOnlyTightWindows] = useState<boolean>(false);
  const [search, setSearch] = useState<string>("");

  const openBookings = useMemo(() => (openBatchId ? (byBatchBookings[openBatchId] || []) : []), [openBatchId, byBatchBookings]);
  const openBatch = useMemo(() => (openBatchId ? batches.find((b) => b.id === openBatchId) || null : null), [openBatchId, batches]);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!companyId) return;
      setLoading(true);
      try {
        const { data: batchRows, error: bErr } = await supabaseUntyped
          .from("booking_batches")
          .select("id, created_at, rows_count, status, raw_text")
          .eq("company_id", companyId)
          .order("created_at", { ascending: false })
          .limit(30);
        if (bErr) throw bErr;

        const batchList: BatchRow[] = (batchRows || []) as BatchRow[];
        const ids = batchList.map((b) => b.id).filter(Boolean);

        const bookingsById: Record<string, BookingRow[]> = {};
        if (ids.length > 0) {
          const { data: bkRows, error: bkErr } = await supabaseUntyped
            .from("bookings")
            .select("id, booking_number, status, service_id, services(name), service_address_text, total_amount, window_start_at, window_end_at, checkout_at, checkin_at, notes, scheduled_date, scheduled_time, batch_id")
            .in("batch_id", ids)
            .order("created_at", { ascending: false });
          if (bkErr) throw bkErr;

          for (const r of (bkRows || []) as RawBookingRow[]) {
            const bid = String(r.batch_id || "");
            if (!bid) continue;
            bookingsById[bid] = bookingsById[bid] || [];
            bookingsById[bid].push({
              id: r.id,
              booking_number: r.booking_number,
              status: r.status,
              service_id: r.service_id,
              service_name: r.services?.name || null,
              service_address_text: r.service_address_text || null,
              total_amount: (typeof r.total_amount === "number" ? r.total_amount : (r.total_amount ? Number(r.total_amount) : null)),
              window_start_at: r.window_start_at || null,
              window_end_at: r.window_end_at || null,
              checkout_at: r.checkout_at || null,
              checkin_at: r.checkin_at || null,
              notes: r.notes || null,
              scheduled_date: r.scheduled_date || null,
              scheduled_time: r.scheduled_time || null,
              batch_id: r.batch_id || null,
            });
          }
        }

        if (!active) return;
        setBatches(batchList);
        setByBatchBookings(bookingsById);
      } catch {
        // non-blocking (dashboard still works)
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [companyId]);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!companyId) return;
      const relevantDates = Object.values(byBatchBookings)
        .flat()
        .map((booking) => booking.scheduled_date || booking.window_start_at || booking.checkin_at || booking.checkout_at || null)
        .filter(Boolean)
        .map((value) => String(value).slice(0, 10));

      if (relevantDates.length === 0) {
        if (active) setCapacityByDate({});
        return;
      }

      const sortedDates = [...new Set(relevantDates)].sort();
      const start = sortedDates[0];
      const end = sortedDates[sortedDates.length - 1];

      try {
        const { data: slotRows } = await supabaseUntyped
          .from("vendor_availability_slots")
          .select("date, slot, capacity, blocked")
          .eq("company_id", companyId)
          .gte("date", start)
          .lte("date", end);

        const nextMap: Record<string, DayCapacitySummary> = {};
        if (Array.isArray(slotRows) && slotRows.length > 0) {
          for (const row of slotRows as RawSlotRow[]) {
            const key = String(row.date);
            if (!nextMap[key]) {
              nextMap[key] = {
                date: key,
                totalCapacity: 0,
                blocked: true,
                configuredSlots: 0,
              };
            }
            nextMap[key].configuredSlots += 1;
            const blocked = !!row.blocked;
            const capacity = Number(row.capacity ?? 0);
            if (!blocked && capacity > 0) {
              nextMap[key].blocked = false;
              nextMap[key].totalCapacity += capacity;
            }
          }
        } else {
          const { data: dayRows } = await supabaseUntyped
            .from("vendor_availability")
            .select("date, capacity, blocked")
            .eq("company_id", companyId)
            .gte("date", start)
            .lte("date", end);

          for (const row of (dayRows || []) as RawDayRow[]) {
            const key = String(row.date);
            nextMap[key] = {
              date: key,
              totalCapacity: Number(row.capacity ?? 0),
              blocked: !!row.blocked || Number(row.capacity ?? 0) <= 0,
              configuredSlots: 0,
            };
          }
        }

        if (active) setCapacityByDate(nextMap);
      } catch {
        if (active) setCapacityByDate({});
      }
    })();

    return () => {
      active = false;
    };
  }, [companyId, byBatchBookings]);

  const batchCards = useMemo(() => {
    return batches.map((b) => {
      const list = byBatchBookings[b.id] || [];
      const statusCounts: Record<string, number> = {};
      const serviceCounts: Record<string, number> = {};
      let totalAmount = 0;
      let minStart: string | null = null;
      let maxEnd: string | null = null;
      let lowCapacityDateCount = 0;
      let closedDateCount = 0;
      const relevantDates = new Set<string>();
      const flagsList: RowRiskFlags[] = [];

      for (const bk of list) {
        statusCounts[bk.status] = (statusCounts[bk.status] || 0) + 1;
        const sName = bk.service_name || t("vendor.bulk.service_fallback");
        serviceCounts[sName] = (serviceCounts[sName] || 0) + 1;
        if (typeof bk.total_amount === "number" && bk.total_amount > 0) totalAmount += bk.total_amount;
        if (bk.window_start_at) {
          if (!minStart || new Date(bk.window_start_at) < new Date(minStart)) minStart = bk.window_start_at;
        }
        if (bk.window_end_at) {
          if (!maxEnd || new Date(bk.window_end_at) > new Date(maxEnd)) maxEnd = bk.window_end_at;
        }
        flagsList.push(
          evaluateRowFlags({
            source: "platform",
            window_start_at: bk.window_start_at || null,
            window_end_at: bk.window_end_at || null,
            scheduled_date: bk.scheduled_date || null,
            scheduled_time: bk.scheduled_time || null,
            checkout_at: bk.checkout_at || null,
            checkin_at: bk.checkin_at || null,
            scheduling_precision: "platform",
          })
        );
        const relevantDate = (bk.scheduled_date || bk.window_start_at || bk.checkin_at || bk.checkout_at || "").slice(0, 10);
        if (relevantDate) relevantDates.add(relevantDate);
      }

      for (const date of relevantDates) {
        const capacity = capacityByDate[date];
        if (!capacity) continue;
        if (capacity.blocked || capacity.totalCapacity <= 0) {
          closedDateCount += 1;
        } else if (capacity.totalCapacity <= 1) {
          lowCapacityDateCount += 1;
        }
      }

      const statusLine = ["pending", "confirmed", "in_progress", "completed", "cancelled"]
        .filter((k) => statusCounts[k])
        .map((k) => `${statusCounts[k]} ${k.replace("_", " ")}`)
        .join(", ");

      const serviceLine = Object.entries(serviceCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map(([name, n]) => `${n}× ${name}`)
        .join(" • ");

      const counts = aggregateRiskCounts(flagsList);
      const riskLabel = computeRiskLabel(counts);
      const suggestedAction = getSuggestedActionLocalized(counts, { t, tx });
      const capacityContext = getBatchCapacityContextLocalized(
        {
          closedDateCount,
          lowCapacityDateCount,
          unscheduledCount: counts.unscheduled,
        },
        { t, tx }
      );

      return {
        batch: b,
        list,
        statusLine,
        serviceLine,
        minStart,
        maxEnd,
        totalAmount,
        tightCount: counts.tight,
        urgentCount: counts.urgent,
        unscheduledCount: counts.unscheduled,
        counts,
        riskLabel,
        suggestedAction,
        capacityContext,
      };
    });
  }, [batches, byBatchBookings, capacityByDate, t, tx]);

  // Sprint 5 — server engine integration for the open batch detail.
  // The card list keeps the client fallback (avoids fan-out of 30+ RPCs);
  // when the user opens a batch we ask the server engine for the canonical
  // summary and per-row flags. The same client computation feeds the
  // `clientFallback` so the UI renders identically when the server is
  // unreachable or the killswitch (`VITE_SCHEDULER_SERVER_DISABLED=1`) is on.
  const openCardSummary = useMemo(
    () => (openBatchId ? batchCards.find((item) => item.batch.id === openBatchId) ?? null : null),
    [openBatchId, batchCards],
  );

  const buildOpenBatchFallback = useCallback((): ClientFallbackInput => {
    if (!openCardSummary) return { rowFlags: [] };
    const rowFlags: RowRiskFlags[] = openCardSummary.list.map((bk) =>
      evaluateRowFlags({
        source: "platform",
        window_start_at: bk.window_start_at || null,
        window_end_at: bk.window_end_at || null,
        scheduled_date: bk.scheduled_date || null,
        scheduled_time: bk.scheduled_time || null,
        checkout_at: bk.checkout_at || null,
        checkin_at: bk.checkin_at || null,
        scheduling_precision: "platform",
      }),
    );
    return { rowFlags };
  }, [openCardSummary]);

  const serverSummary = useSchedulerSummary({
    companyId,
    batchId: openBatchId,
    clientFallback: buildOpenBatchFallback,
    enabled: !!openBatchId,
  });

  const serverRows = useBatchSchedulerRows({
    companyId,
    batchId: openBatchId,
    enabled: !!openBatchId,
  });

  const serverRowFlagsByBookingId = useMemo(() => {
    const map = new Map<string, { tight: boolean; urgent: boolean; unscheduled: boolean; sameDay: boolean }>();
    for (const r of serverRows.data ?? []) {
      map.set(r.booking_id, {
        tight: r.tight_window,
        urgent: r.urgent,
        unscheduled: r.unscheduled,
        sameDay: r.same_day_turnover,
      });
    }
    return map;
  }, [serverRows.data]);

  // Analytics: log scheduler summary view once per (open batch, source) tuple.
  useEffect(() => {
    if (!openBatchId || !companyId) return;
    if (!serverSummary.data || !serverSummary.source) return;
    trackSchedulerSummaryViewed({
      surface: "bulk_orders_panel",
      scope: "batch",
      source: serverSummary.source,
      companyId,
      batchId: openBatchId,
      summary: serverSummary.data,
    });
    trackSchedulerActionShown({
      surface: "bulk_orders_panel",
      scope: "batch",
      actionId: "batch_suggested_action",
      actionLabel: serverSummary.data.suggested_action,
      source: serverSummary.source,
      companyId,
      batchId: openBatchId,
      riskLabel: serverSummary.data.risk_label,
      reasonCodes: serverSummary.data.risk_reasons.map((r) => r.code),
    });
  }, [openBatchId, companyId, serverSummary.data, serverSummary.source]);

  const filteredOpenBookings = useMemo(() => {
    let list = [...openBookings];
    if (filterStatus !== "all") list = list.filter((b) => String(b.status) === filterStatus);
    if (onlyTightWindows) {
      list = list.filter((b) => {
        const h = hoursBetween(b.window_start_at || null, b.window_end_at || null);
        return h !== null && h <= 2; // heuristic: <=2 hours considered tight
      });
    }
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((b) => (b.service_address_text || "").toLowerCase().includes(q) || (b.booking_number || "").toLowerCase().includes(q));
    }
    return list;
  }, [openBookings, filterStatus, onlyTightWindows, search]);

  return (
    <>
      <Card className="border-gray-200 shadow-card overflow-hidden">
        <CardHeader>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <CardTitle className="flex items-center gap-2 break-words">
                {t("vendor.bulk.title")} <Badge variant="secondary">{t("vendor.bulk.beta")}</Badge>
              </CardTitle>
              <CardDescription className="break-words">
                {t("vendor.bulk.desc")}
              </CardDescription>
            </div>
            <Button variant="outline" onClick={() => navigate("/company/dashboard?tab=calendar")}>
              {t("vendor.bulk.go_calendar")}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="rounded-xl border border-primary/10 bg-primary/5 p-4">
            <div className="text-sm font-semibold text-gray-900">{t("vendor.bulk.scheduler_intro_title")}</div>
            <p className="mt-1 text-sm text-gray-600">
              {t("vendor.bulk.scheduler_intro_desc")}
            </p>
          </div>
          {loading ? (
            <div className="text-sm text-muted-foreground">{t('vendor.bulk.loading')}</div>
          ) : batchCards.length === 0 ? (
            <div className="text-sm text-muted-foreground">{t('vendor.bulk.empty')}</div>
          ) : (
            batchCards.map(({ batch, statusLine, serviceLine, minStart, maxEnd, list, totalAmount, counts, riskLabel, suggestedAction, capacityContext }) => (
              <div key={batch.id} className="border rounded-xl p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="border-primary text-primary">
                      BULK
                    </Badge>
                    <Badge variant="outline" className={RISK_LABEL_BADGE_CLASS[riskLabel]}>
                      {riskLabel}
                    </Badge>
                    <div className="font-medium break-words">Batch {batch.id.slice(0, 8)}</div>
                    <div className="text-xs text-muted-foreground">{fmt(batch.created_at)}</div>
                  </div>
                  <div className="text-sm text-muted-foreground mt-1 break-words">
                    {tx("vendor.bulk.rows", { count: list.length })} • {statusLine || "—"}
                  </div>
                  <div className="text-sm mt-1 break-words">
                    <span className="text-muted-foreground">{t("vendor.bulk.total")}</span>{" "}
                    <span className="font-medium">{totalAmount > 0 ? `${totalAmount.toFixed(2)}€` : "—"}</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-1 break-words">
                    {tx("vendor.bulk.window", { start: fmt(minStart), end: fmt(maxEnd) })}
                  </div>
                  <div className="text-xs text-muted-foreground mt-1 break-words">
                    {tx("vendor.bulk.scheduler_signals", { summary: getCountsSummaryLocalized(counts, { t, tx }) })}
                  </div>
                  {serviceLine ? (
                    <div className="text-xs text-muted-foreground mt-1 break-words">{tx("vendor.bulk.services", { list: serviceLine })}</div>
                  ) : null}
                  <div className="text-xs mt-1 break-words text-gray-700">{suggestedAction}</div>
                  {capacityContext ? <div className="text-xs mt-1 break-words text-amber-700">{capacityContext}</div> : null}
                </div>
                <div className="flex gap-2 flex-wrap sm:justify-end">
                  <Button variant="outline" onClick={() => setOpenBatchId(batch.id)}>
                    {t("vendor.bulk.open")}
                  </Button>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <Sheet open={!!openBatchId} onOpenChange={(o) => !o && setOpenBatchId(null)}>
        <SheetContent className="w-full sm:max-w-3xl max-h-[100vh] overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="break-words">
              {openBatchId ? tx("vendor.bulk.sheet_title", { id: openBatchId.slice(0, 8) }) : t("vendor.bulk.title")}
            </SheetTitle>
            <SheetDescription className="break-words">
              {openBatch ? tx("vendor.bulk.sheet_created", { date: fmt(openBatch.created_at), count: openBatch.rows_count }) : ""}
            </SheetDescription>
          </SheetHeader>

          <div className="mt-5 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-1">
                <div className="text-xs text-muted-foreground mb-1">{t("vendor.bulk.filter_status")}</div>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("vendor.bulk.filter_all")}</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="in_progress">In progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-1">
                <div className="text-xs text-muted-foreground mb-1">{t("vendor.bulk.search")}</div>
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t("vendor.bulk.search_ph")} />
              </div>
              <div className="sm:col-span-1 flex items-end">
                <Button
                  variant={onlyTightWindows ? "default" : "outline"}
                  className="w-full"
                  onClick={() => setOnlyTightWindows((v) => !v)}
                >
                  {onlyTightWindows ? t("vendor.bulk.tight_on") : t("vendor.bulk.tight_only")}
                </Button>
              </div>
            </div>

            <Separator />

            <div className="text-sm text-muted-foreground">
              {t("vendor.bulk.tip")}
            </div>

            {openBatch ? (() => {
              const localSummary = batchCards.find((item) => item.batch.id === openBatch.id);
              if (!localSummary) return null;
              // Prefer the server engine result when available; fall back to
              // the local computation otherwise. Both shapes are unified by
              // `useSchedulerSummary` (server flips to `client_fallback`).
              const serverData = serverSummary.data;
              const riskLabel = serverData?.risk_label ?? localSummary.riskLabel;
              const counts = serverData?.counts ?? localSummary.counts;
              const suggestedAction = getSuggestedActionLocalized(
                counts,
                { t, tx },
                serverData?.day_flags
              );
              const reasons = serverData?.risk_reasons ?? [];
              const confidenceLevel = serverData ? getConfidenceLevel(serverData.confidence_score) : null;
              const sourceLabel =
                serverSummary.source === "server"
                  ? t("vendor.bulk.source_server")
                  : serverSummary.source === "client_fallback"
                  ? t("vendor.bulk.source_fallback")
                  : t("vendor.bulk.source_local");
              return (
                <div className="rounded-xl border border-primary/10 bg-primary/5 p-4 space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-sm font-semibold text-gray-900">{t("vendor.bulk.scheduler_summary")}</div>
                    <Badge variant="outline" className={RISK_LABEL_BADGE_CLASS[riskLabel]}>
                      {riskLabel}
                    </Badge>
                    {confidenceLevel ? (
                      <Badge
                        variant="outline"
                        className={CONFIDENCE_BADGE_CLASS[confidenceLevel]}
                        title={getConfidenceCopyLocalized(confidenceLevel, { t, tx })}
                      >
                        {tx("vendor.bulk.confidence", { level: t(`scheduler.confidence_level.${confidenceLevel}`) })}
                      </Badge>
                    ) : null}
                  </div>
                  <div className="text-sm text-gray-700">
                    {tx("vendor.bulk.scheduler_signals", { summary: getCountsSummaryLocalized(counts, { t, tx }) })}
                    {counts.same_day > 0 ? ` • ${counts.same_day} same-day turnover` : ""}
                  </div>
                  {reasons.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {reasons.map((r) => (
                        <Badge
                          key={`${r.code}-${r.scope}`}
                          variant="outline"
                          className="border-slate-300 text-slate-700 text-[10px]"
                        >
                          {formatRiskReasonLocalized(r, { t, tx })}
                        </Badge>
                      ))}
                    </div>
                  ) : null}
                  <div className="text-sm text-gray-700">{suggestedAction}</div>
                  {localSummary.capacityContext ? (
                    <div className="text-sm text-amber-700">{localSummary.capacityContext}</div>
                  ) : null}
                  <div className="text-[10px] text-gray-500">
                    {tx("vendor.bulk.source_label", {
                      source: sourceLabel,
                      extra: serverSummary.isLoading ? ` • ${t("vendor.bulk.updating")}` : "",
                    })}
                  </div>
                </div>
              );
            })() : null}

            <div className="space-y-3">
              {filteredOpenBookings.length === 0 ? (
                <div className="text-sm text-muted-foreground">{t('vendor.bulk.no_rows')}</div>
              ) : (
                filteredOpenBookings.map((b) => {
                  // Prefer server per-row flags when available; fall back to
                  // a simple in-memory window check (preserves the previous
                  // visual contract while the engine warms up).
                  const serverFlags = serverRowFlagsByBookingId.get(b.id);
                  const tight = serverFlags
                    ? serverFlags.tight
                    : (() => {
                        const h = hoursBetween(b.window_start_at || null, b.window_end_at || null);
                        return h !== null && h <= 2;
                      })();
                  return (
                    <div key={b.id} className={cn("border rounded-xl p-3", tight ? "border-amber-200 bg-amber-50/40" : "border-gray-200")}>
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="outline" className="border-primary text-primary">
                              BULK
                            </Badge>
                            <Badge className="bg-gray-100 text-gray-800">{String(b.status || "").replace("_", " ")}</Badge>
                            <div className="font-medium break-words">{b.booking_number}</div>
                            {serverFlags?.urgent ? (
                              <Badge variant="outline" className="border-red-300 text-red-700 text-[10px]">
                                {t("vendor.bulk.urgent_badge")}
                              </Badge>
                            ) : null}
                            {serverFlags?.tight ? (
                              <Badge variant="outline" className="border-amber-300 text-amber-700 text-[10px]">
                                {t("vendor.bulk.tight_badge")}
                              </Badge>
                            ) : null}
                            {serverFlags?.unscheduled ? (
                              <Badge variant="outline" className="border-slate-300 text-slate-700 text-[10px]">
                                {t("vendor.bulk.unscheduled_badge")}
                              </Badge>
                            ) : null}
                            {serverFlags?.sameDay ? (
                              <Badge variant="outline" className="border-purple-300 text-purple-700 text-[10px]">
                                same-day
                              </Badge>
                            ) : null}
                          </div>
                          <div className="text-sm mt-1 break-words">{b.service_name || t("vendor.bulk.service_fallback")}</div>
                          <div className="text-xs text-muted-foreground mt-1 break-words">{b.service_address_text || "—"}</div>
                          <div className="text-xs text-muted-foreground mt-1 break-words">
                            {t("vendor.bulk.price")} <span className="font-medium text-gray-900">{typeof b.total_amount === "number" && b.total_amount > 0 ? `${b.total_amount.toFixed(2)}€` : "—"}</span>
                          </div>
                          <div className="text-xs text-muted-foreground mt-1 break-words">
                            {t("vendor.bulk.window_row")} {fmt(b.window_start_at)} → {fmt(b.window_end_at)}
                          </div>
                          {b.notes ? <div className="text-xs text-muted-foreground mt-1 break-words">{t("vendor.bulk.notes")} {b.notes}</div> : null}
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          <Button variant="outline" size="sm" onClick={() => navigate(`/booking/${b.id}`)}>
                            {t("vendor.bulk.open_booking")}
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => navigate(`/company/dashboard?tab=calendar`)}>
                            {t("vendor.bulk.assign_time")}
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

